import { Head, Link, useForm } from "@inertiajs/react"
import { ArrowLeft } from "@untitledui/icons"

import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { InputBase, Label, TextField } from "@/components/ui/input"
import { Notice } from "@/components/ui/notice"
import { NativeSelect } from "@/components/ui/select"
import { AdminLayout } from "@/layouts/admin-layout"
import { toFieldError } from "@/lib/utils"
import { adminEnquiriesPath } from "@/routes"

interface Props {
  sources: string[]
  states: string[]
}

interface FormData {
  first_name: string
  last_name: string
  email: string
  phone: string
  school_name_as_typed: string
  state: string
  year_level_interest: string
  program_interest: string
  source: string
  consent_programs_comms: boolean
  [key: string]: string | boolean
}

/** The intake form for a call or an email staff took. The website form posts here later. */
export default function AdminEnquiriesNew({ sources, states }: Props) {
  const { data, setData, post, processing, errors, transform } =
    useForm<FormData>({
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      school_name_as_typed: "",
      state: "NSW",
      year_level_interest: "",
      program_interest: "",
      source: sources[0] ?? "phone",
      consent_programs_comms: false,
    })
  const baseError = (errors as Record<string, string | undefined>).base

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    transform((d) => ({ enquiry: d }))
    post(adminEnquiriesPath())
  }

  const field = (
    name: keyof FormData & string,
    label: string,
    placeholder?: string,
    type = "text",
  ) => (
    <TextField
      name={name}
      value={String(data[name])}
      onChange={(v: string) => setData(name, v)}
      isInvalid={!!errors[name]}
      error={toFieldError(
        typeof errors[name] === "string" ? errors[name] : undefined,
      )}
    >
      <Label>{label}</Label>
      <InputBase placeholder={placeholder} size="md" type={type} />
    </TextField>
  )

  return (
    <AdminLayout>
      <Head title="File an enquiry" />
      <div className="mx-auto flex max-w-3xl flex-col gap-6 px-4 lg:px-8">
        <Link
          href={adminEnquiriesPath()}
          className="text-tertiary hover:text-secondary inline-flex w-max items-center gap-1.5 text-sm font-medium transition-colors"
        >
          <ArrowLeft className="size-4" /> Back to enquiries
        </Link>
        <header className="flex flex-col gap-1">
          <h1 className="text-display-xs md:text-display-sm text-primary">
            File an enquiry
          </h1>
          <p className="text-tertiary text-md">
            A person who found us before they belong to a school. The school
            they name is matched to the mirror; a human confirms it.
          </p>
        </header>
        {baseError && (
          <Notice tone="error" role="alert">
            {baseError}
          </Notice>
        )}
        <form onSubmit={submit} className="flex flex-col gap-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {field("first_name", "First name")}
            {field("last_name", "Last name")}
            {field("email", "Email", "name@school.nsw.edu.au", "email")}
            {field("phone", "Phone", "04…", "tel")}
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_140px]">
            {field(
              "school_name_as_typed",
              "School, as they said it",
              "Engadine High School",
            )}
            <NativeSelect
              label="State"
              value={data.state}
              onChange={(e) => setData("state", e.target.value)}
              options={states.map((s) => ({ label: s, value: s }))}
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {field(
              "year_level_interest",
              "Year level they asked about",
              "Year 9",
            )}
            {field(
              "program_interest",
              "Program they asked about",
              "batyr@school",
            )}
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <NativeSelect
              label="How it arrived"
              value={data.source}
              onChange={(e) => setData("source", e.target.value)}
              options={sources.map((s) => ({
                label: s.replace(/_/g, " "),
                value: s,
              }))}
            />
            <Checkbox
              isSelected={data.consent_programs_comms}
              onChange={(v: boolean) => setData("consent_programs_comms", v)}
              label="Happy to hear from the programs team"
            />
          </div>
          <div className="flex justify-end">
            <Button
              size="md"
              color="primary"
              type="submit"
              isLoading={processing}
            >
              File enquiry
            </Button>
          </div>
        </form>
      </div>
    </AdminLayout>
  )
}
